import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';

import {
  CONSOLE_DISPLAY_CAP,
  consoleRhythm,
  consoleTokens,
} from './consoleTokens';

export type ConsoleRow = Record<string, unknown>;
export type ConsoleAggregates = Record<string, unknown>;

export interface ConsoleColumnDef {
  key: string;
  /** Column header; rendered uppercase, fainter. */
  label: string;
  /** Minimum content width in px; the column grows to fit content. */
  minWidth?: number;
  align?: 'left' | 'right';
  /** Absorbs spare table width and wraps (long free text, e.g. notes). */
  fill?: boolean;
  render: (row: ConsoleRow) => React.ReactNode;
  /**
   * Cell for group bands and the Σ footer. Columns without it are spanned
   * by the band label, so declare it on trailing (usually money) columns.
   */
  renderAggregate?: (aggregates: ConsoleAggregates, emphasized: boolean) => React.ReactNode;
}

/** Structurally matches the run contract's group tree (snake_case kept). */
export interface ConsoleGroup {
  key: string;
  label?: string | null;
  aggregates: ConsoleAggregates;
  row_count: number;
  rows: ConsoleRow[];
  subgroups?: ConsoleGroup[] | null;
}

export interface ConsoleTableProps {
  columns: ConsoleColumnDef[];
  groups: ConsoleGroup[];
  /** Σ footer aggregates; the footer is always rendered when present. */
  totalsAggregates?: ConsoleAggregates;
  /** Left side of the Σ footer, e.g. `Σ 73 sessions · 3 clinicians`. */
  totalsLabel?: React.ReactNode;
  /** Inline meta after a band label, e.g. `24 · 1 unsigned`. */
  bandMeta?: (group: ConsoleGroup) => React.ReactNode;
  /** Inline meta after a sub-band label. Defaults to bandMeta. */
  subBandMeta?: (group: ConsoleGroup) => React.ReactNode;
  rowKey: (row: ConsoleRow, index: number) => string;
  /** Noun in "+N more <noun>" expanders. */
  moreRowsNoun?: string;
  /** Detail rows shown per deepest group before "+N more". */
  displayCap?: number;
  /** Label for groups whose dimension value is missing. */
  nullGroupLabel?: string;
  loading?: boolean;
  /**
   * Re-running with data already on screen: dims the table, blocks
   * interaction, and floats a `running…` chip. Use `loading` only for the
   * first load (skeletons); never blank data the user already has.
   */
  refreshing?: boolean;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  /** px offset for the sticky column header (height of any sticky bar above). */
  stickyHeaderOffset?: number;
  /** Fired when a band collapses/expands (analytics hook). */
  onGroupToggle?: (groupKey: string, collapsed: boolean) => void;
  /** Active row-sort column key (sorting happens server-side). */
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  /**
   * Makes headers clickable: cycles asc → desc → null (dataset order).
   * Sorting applies to rows within bands; band order is unaffected.
   */
  onSortChange?: (key: string | null, dir: 'asc' | 'desc') => void;
}

/**
 * The console data table, rendered as a semantic <table> so every row shares
 * one layout context: columns can never drift out of sync with headers, the
 * header row is position-sticky, and the Σ footer pins to the viewport
 * bottom. A zero-content filler column absorbs horizontal slack between the
 * left data cluster and the first right-aligned column, so data columns hug
 * their content instead of one of them inheriting all the whitespace.
 */
export const ConsoleTable: React.FC<ConsoleTableProps> = ({
  columns,
  groups,
  totalsAggregates,
  totalsLabel,
  bandMeta,
  subBandMeta,
  rowKey,
  moreRowsNoun = 'rows',
  displayCap = CONSOLE_DISPLAY_CAP,
  nullGroupLabel = 'Unassigned',
  loading = false,
  refreshing = false,
  emptyMessage = 'No rows in this window.',
  emptyAction,
  stickyHeaderOffset = 0,
  onGroupToggle,
  sortKey = null,
  sortDir = 'asc',
  onSortChange,
}) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Slack absorber: a declared fill column (long free text, wraps) when one
  // is visible, else a synthetic empty filler before the first right-aligned
  // or aggregate column.
  const fillIndex = useMemo(() => columns.findIndex(column => column.fill), [columns]);
  const fillerIndex = useMemo(() => {
    if (fillIndex !== -1) return -1; // the fill column takes the slack
    const index = columns.findIndex(
      column => column.align === 'right' || column.renderAggregate,
    );
    return index === -1 ? columns.length : index;
  }, [columns, fillIndex]);
  // The band label spans every leading column without an aggregate cell.
  const aggStart = useMemo(() => {
    const index = columns.findIndex(column => column.renderAggregate);
    return index === -1 ? columns.length : index;
  }, [columns]);
  const labelColSpan =
    aggStart + (fillerIndex !== -1 && fillerIndex <= aggStart ? 1 : 0);
  const totalCells = columns.length + (fillerIndex === -1 ? 0 : 1);

  // A lone label-less group is an ungrouped run: render rows without a band.
  const bandless = groups.length === 1 && groups[0].label == null && !groups[0].subgroups;

  const resolvedSubBandMeta = subBandMeta ?? bandMeta;

  const toggleGroup = (groupKey: string) => {
    setCollapsed(previous => {
      const next = new Set(previous);
      const nowCollapsed = !next.has(groupKey);
      if (nowCollapsed) {
        next.add(groupKey);
      } else {
        next.delete(groupKey);
      }
      onGroupToggle?.(groupKey, nowCollapsed);
      return next;
    });
  };

  const cellBaseSx = {
    borderBottom: `1px solid ${consoleTokens.rowLine}`,
    px: 1,
    whiteSpace: 'nowrap' as const,
  };

  const filler = <Box component="td" aria-hidden sx={{ ...cellBaseSx, width: '100%' }} />;

  // The filler always falls inside the label span (its index is never past
  // the first aggregate column), so band/footer rows only render data cells.
  const aggregateCells = (
    aggregates: ConsoleAggregates,
    emphasized: boolean,
    sx: object,
  ) => (
    <>
      {columns.slice(aggStart).map(column => (
        <Box
          component="td"
          key={column.key}
          sx={{ ...sx, textAlign: column.align ?? 'left' }}
        >
          {column.renderAggregate
            ? column.renderAggregate(aggregates, emphasized)
            : null}
        </Box>
      ))}
    </>
  );

  const renderDetailRows = (node: ConsoleGroup, pathKey: string, indent: boolean) => {
    const isExpanded = expanded.has(pathKey);
    const visibleRows = isExpanded ? node.rows : node.rows.slice(0, displayCap);
    const hiddenCount = node.rows.length - visibleRows.length;
    return (
      <>
        {visibleRows.map((row, index) => (
          <Box
            component="tr"
            key={rowKey(row, index)}
            sx={{ '&:hover td': { bgcolor: consoleTokens.bandBg } }}
          >
            {columns.map((column, columnIndex) => (
              <React.Fragment key={column.key}>
                {columnIndex === fillerIndex && filler}
                <Box
                  component="td"
                  sx={{
                    ...cellBaseSx,
                    height: consoleRhythm.row,
                    pl: indent && columnIndex === 0 ? 3.5 : 1,
                    textAlign: column.align ?? 'left',
                    ...(column.fill
                      ? { whiteSpace: 'normal', width: '100%' }
                      : {}),
                  }}
                >
                  {column.render(row)}
                </Box>
              </React.Fragment>
            ))}
            {fillerIndex === columns.length && filler}
          </Box>
        ))}
        {hiddenCount > 0 && (
          <Box component="tr">
            <Box
              component="td"
              colSpan={totalCells}
              onClick={() => setExpanded(previous => new Set(previous).add(pathKey))}
              sx={{
                ...cellBaseSx,
                cursor: 'pointer',
                height: 30,
                '&:hover': { bgcolor: consoleTokens.bandBg },
              }}
            >
              <Typography
                sx={{
                  color: consoleTokens.fainter,
                  fontFamily: consoleTokens.mono,
                  fontSize: '0.6875rem',
                  pl: indent ? 2.5 : 0,
                }}
              >
                + {hiddenCount} more {moreRowsNoun}
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );
  };

  // asc → desc → back to dataset order.
  const cycleSort = (key: string) => {
    if (!onSortChange) return;
    if (sortKey !== key) {
      onSortChange(key, 'asc');
    } else if (sortDir === 'asc') {
      onSortChange(key, 'desc');
    } else {
      onSortChange(null, 'asc');
    }
  };

  const headerRow = (
    <Box component="thead">
      <Box component="tr">
        {columns.map((column, columnIndex) => {
          const isSorted = sortKey === column.key;
          return (
          <React.Fragment key={column.key}>
            {columnIndex === fillerIndex && (
              <Box
                component="th"
                aria-hidden
                sx={{
                  bgcolor: '#FFFFFF',
                  borderBottom: `1px solid ${consoleTokens.line}`,
                  position: 'sticky',
                  top: stickyHeaderOffset,
                  width: '100%',
                  zIndex: 2,
                }}
              />
            )}
            <Box
              component="th"
              onClick={onSortChange ? () => cycleSort(column.key) : undefined}
              aria-sort={
                isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
              }
              sx={{
                bgcolor: '#FFFFFF',
                borderBottom: `1px solid ${consoleTokens.line}`,
                cursor: onSortChange ? 'pointer' : 'default',
                height: consoleRhythm.columnHeader,
                minWidth: column.minWidth,
                position: 'sticky',
                px: 1,
                top: stickyHeaderOffset,
                whiteSpace: 'nowrap',
                width: column.fill ? '100%' : undefined,
                zIndex: 2,
                '&:hover p': onSortChange ? { color: consoleTokens.dim } : undefined,
              }}
            >
              <Typography
                sx={{
                  color: isSorted ? consoleTokens.ink : consoleTokens.fainter,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textAlign: column.align ?? 'left',
                  textTransform: 'uppercase',
                }}
              >
                {column.label}
                {isSorted && (
                  <Box component="span" sx={{ fontSize: '0.5625rem', ml: 0.5 }}>
                    {sortDir === 'asc' ? '▲' : '▼'}
                  </Box>
                )}
              </Typography>
            </Box>
          </React.Fragment>
          );
        })}
        {fillerIndex === columns.length && (
          <Box
            component="th"
            aria-hidden
            sx={{
              bgcolor: '#FFFFFF',
              borderBottom: `1px solid ${consoleTokens.line}`,
              position: 'sticky',
              top: stickyHeaderOffset,
              width: '100%',
              zIndex: 2,
            }}
          />
        )}
      </Box>
    </Box>
  );

  const tableSx = {
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    tableLayout: 'auto' as const,
    width: '100%',
  };

  if (loading) {
    return (
      <Box component="table" sx={tableSx}>
        {headerRow}
        <Box component="tbody">
          {Array.from({ length: 8 }, (_, index) => (
            <Box component="tr" key={index}>
              {columns.map((column, columnIndex) => (
                <React.Fragment key={column.key}>
                  {columnIndex === fillerIndex && filler}
                  <Box component="td" sx={{ ...cellBaseSx, height: consoleRhythm.row }}>
                    <Skeleton variant="text" sx={{ fontSize: '0.75rem' }} />
                  </Box>
                </React.Fragment>
              ))}
              {fillerIndex === columns.length && filler}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Refetch veil: old data stays visible but dimmed and inert; a sticky
  // `running…` chip stays in view however tall the table is. Quick re-runs
  // ease in/out instead of flashing.
  const withRefetchVeil = (content: React.ReactNode) => (
    <Box aria-busy={refreshing || undefined} sx={{ position: 'relative' }}>
      <Box
        sx={{
          opacity: refreshing ? 0.45 : 1,
          pointerEvents: refreshing ? 'none' : 'auto',
          transition: 'opacity 160ms ease',
        }}
      >
        {content}
      </Box>
      {refreshing && (
        <Box sx={{ inset: 0, position: 'absolute', zIndex: 3 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              maxHeight: '70vh',
              position: 'sticky',
              top: stickyHeaderOffset,
            }}
          >
            <Box
              role="status"
              sx={{
                alignItems: 'center',
                bgcolor: '#FFFFFF',
                border: `1px solid ${consoleTokens.line}`,
                borderRadius: '6px',
                boxShadow: '0 2px 10px rgba(38, 34, 46, 0.08)',
                display: 'flex',
                gap: 1,
                px: 1.5,
                py: 0.75,
              }}
            >
              <CircularProgress
                size={12}
                thickness={5}
                sx={{
                  color: consoleTokens.ink,
                  '@media (prefers-reduced-motion: reduce)': { display: 'none' },
                }}
              />
              <Typography
                sx={{
                  color: consoleTokens.dim,
                  fontFamily: consoleTokens.mono,
                  fontSize: '0.6875rem',
                }}
              >
                running…
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  const isEmpty = groups.every(group =>
    group.subgroups
      ? group.subgroups.every(subgroup => subgroup.rows.length === 0)
      : group.rows.length === 0,
  );
  if (isEmpty) {
    return withRefetchVeil(
      <Box sx={{ px: 2, py: 8, textAlign: 'center' }}>
        <Typography sx={{ color: consoleTokens.dim, fontSize: '0.8125rem', mb: emptyAction ? 2 : 0 }}>
          {emptyMessage}
        </Typography>
        {emptyAction}
      </Box>,
    );
  }

  return withRefetchVeil(
    <Box component="table" sx={tableSx}>
      {headerRow}
      <Box component="tbody">
        {bandless
          ? renderDetailRows(groups[0], groups[0].key, false)
          : groups.map(group => {
              const isCollapsed = collapsed.has(group.key);
              const ToggleIcon = isCollapsed
                ? KeyboardArrowRightIcon
                : KeyboardArrowDownIcon;
              const bandCellSx = {
                bgcolor: consoleTokens.bandBg,
                borderBottom: `1px solid ${consoleTokens.line}`,
                height: consoleRhythm.band,
                px: 1,
                whiteSpace: 'nowrap' as const,
              };
              return (
                <React.Fragment key={group.key}>
                  {/* Level-1 band: label + meta, aggregates on the band */}
                  <Box
                    component="tr"
                    onClick={() => toggleGroup(group.key)}
                    role="button"
                    aria-expanded={!isCollapsed}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box component="td" colSpan={labelColSpan} sx={bandCellSx}>
                      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                        <ToggleIcon sx={{ color: consoleTokens.dim, fontSize: 14 }} />
                        <Typography
                          noWrap
                          sx={{ color: consoleTokens.ink, fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          {group.label ?? nullGroupLabel}
                        </Typography>
                        {bandMeta && (
                          <Typography
                            component="span"
                            noWrap
                            sx={{
                              color: consoleTokens.fainter,
                              fontFamily: consoleTokens.mono,
                              fontSize: '0.6875rem',
                              ml: 0.5,
                            }}
                          >
                            {bandMeta(group)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {aggregateCells(group.aggregates, true, bandCellSx)}
                  </Box>

                  {!isCollapsed &&
                    (group.subgroups
                      ? group.subgroups.map(subgroup => {
                          const subBandCellSx = {
                            bgcolor: consoleTokens.subBandBg,
                            borderBottom: `1px solid ${consoleTokens.rowLine}`,
                            height: consoleRhythm.subBand,
                            px: 1,
                            whiteSpace: 'nowrap' as const,
                          };
                          return (
                            <React.Fragment key={subgroup.key}>
                              {/* Level-2 sub-band: indented, quieter */}
                              <Box component="tr">
                                <Box
                                  component="td"
                                  colSpan={labelColSpan}
                                  sx={subBandCellSx}
                                >
                                  <Box
                                    sx={{ alignItems: 'center', display: 'flex', gap: 0.5, pl: 2.5 }}
                                  >
                                    <Typography
                                      noWrap
                                      sx={{
                                        color: consoleTokens.ink,
                                        fontFamily: consoleTokens.mono,
                                        fontSize: '0.6875rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {subgroup.label ?? nullGroupLabel}
                                    </Typography>
                                    {resolvedSubBandMeta && (
                                      <Typography
                                        component="span"
                                        noWrap
                                        sx={{
                                          color: consoleTokens.fainter,
                                          fontFamily: consoleTokens.mono,
                                          fontSize: '0.6875rem',
                                          ml: 0.5,
                                        }}
                                      >
                                        {resolvedSubBandMeta(subgroup)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                {aggregateCells(subgroup.aggregates, false, subBandCellSx)}
                              </Box>
                              {renderDetailRows(
                                subgroup,
                                `${group.key}/${subgroup.key}`,
                                true,
                              )}
                            </React.Fragment>
                          );
                        })
                      : renderDetailRows(group, group.key, false))}
                </React.Fragment>
              );
            })}
      </Box>

      {/* Sticky Σ footer */}
      {totalsAggregates && (
        <Box component="tfoot">
          <Box component="tr">
            {(() => {
              const footerCellSx = {
                bgcolor: '#FFFFFF',
                borderTop: `1px solid ${consoleTokens.ink}`,
                bottom: 0,
                boxShadow: '0 -4px 12px rgba(38, 34, 46, 0.06)',
                height: consoleRhythm.footer,
                position: 'sticky' as const,
                px: 1,
                whiteSpace: 'nowrap' as const,
                zIndex: 2,
              };
              return (
                <>
                  <Box component="td" colSpan={labelColSpan} sx={footerCellSx}>
                    <Typography
                      sx={{ color: consoleTokens.ink, fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      {totalsLabel}
                    </Typography>
                  </Box>
                  {aggregateCells(totalsAggregates, true, footerCellSx)}
                </>
              );
            })()}
          </Box>
        </Box>
      )}
    </Box>,
  );
};
